/**
 * Team Grid — Expandable bio panels, one open at a time.
 *
 * Bio panels use CSS grid-template-rows: 0fr → 1fr for smooth
 * height animation.  The hidden attribute is toggled for a11y,
 * with a reflow forced between removing hidden and adding the
 * open class so the browser sees the 0fr starting state.
 *
 * State is tracked with a generation counter so that stale
 * transitionend listeners from rapid clicks are safely ignored.
 * When switching between people, the old panel animates closed
 * before the new one animates open.
 *
 * @package DCLT
 */

( function () {
	'use strict';

	var OPEN_CLASS = 'dclt-team-grid__bio-panel--open';

	function initGrid( container ) {
		var grid  = container.querySelector( '.dclt-team-grid__grid' );
		var cards = container.querySelectorAll( '[data-team-card]' );

		if ( ! grid || ! cards.length ) return;

		var activePanel = null;
		var activeCard  = null;
		var generation  = 0; // Incremented on every state change; stale listeners bail out.
		var pendingOpen = null; // Queued index to open after a close animation finishes.

		/**
		 * Animate the active panel closed. Calls onDone when the
		 * transition finishes (if not superseded by a newer action).
		 */
		function closePanel( onDone ) {
			if ( ! activePanel ) {
				if ( onDone ) onDone();
				return;
			}

			var closingPanel = activePanel;
			var closingCard  = activeCard;
			var gen          = ++generation;

			closingPanel.classList.remove( OPEN_CLASS );

			if ( closingCard ) {
				closingCard.setAttribute( 'aria-expanded', 'false' );
			}

			activePanel = null;
			activeCard  = null;

			closingPanel.addEventListener( 'transitionend', function onEnd( e ) {
				if ( e.propertyName !== 'grid-template-rows' ) return;
				closingPanel.removeEventListener( 'transitionend', onEnd );

				// Only act if no newer action has superseded this one.
				if ( generation !== gen ) return;

				closingPanel.hidden = true;

				if ( onDone ) onDone();
			} );
		}

		/**
		 * Perform the actual open animation for a panel (no close logic).
		 */
		function doOpen( index ) {
			var card  = container.querySelector( '[data-team-card="' + index + '"]' );
			var panel = container.querySelector( '[data-bio-panel="' + index + '"]' );

			if ( ! card || ! panel ) return;

			var gen = ++generation;

			// Compute visual row based on current grid column count.
			var colCount = window.getComputedStyle( grid )
				.getPropertyValue( 'grid-template-columns' )
				.split( ' ' ).length;
			var cardIndex = parseInt( index, 10 );
			var rowEnd    = Math.min(
				( Math.floor( cardIndex / colCount ) + 1 ) * colCount - 1,
				container.querySelectorAll( '[data-team-card]' ).length - 1
			);
			var lastInRow = container.querySelectorAll( '[data-team-card]' )[ rowEnd ];

			// Move panel after the last card in this visual row.
			if ( lastInRow ) {
				lastInRow.after( panel );
			}

			// Show panel at 0fr, force reflow, then animate to 1fr.
			panel.hidden = false;
			void panel.offsetHeight;
			panel.classList.add( OPEN_CLASS );

			card.setAttribute( 'aria-expanded', 'true' );
			activePanel = panel;
			activeCard  = card;

			// Smooth scroll after animation completes.
			panel.addEventListener( 'transitionend', function onScroll( e ) {
				if ( e.propertyName !== 'grid-template-rows' ) return;
				panel.removeEventListener( 'transitionend', onScroll );
				if ( generation !== gen ) return;
				panel.scrollIntoView( { behavior: 'smooth', block: 'nearest' } );
			} );
		}

		/**
		 * Open a specific person's bio panel by index.
		 * If another panel is already open, close it first (animated),
		 * then open the new one after the close transition completes.
		 */
		function openPanel( index ) {
			var card  = container.querySelector( '[data-team-card="' + index + '"]' );
			var panel = container.querySelector( '[data-bio-panel="' + index + '"]' );

			if ( ! card || ! panel ) return;

			// Cancel any previously queued open.
			pendingOpen = null;

			// Toggle off if the same panel is already open.
			if ( activePanel === panel ) {
				closePanel();
				return;
			}

			// If a different panel is open, animate it closed first,
			// then open the new one when the close finishes.
			if ( activePanel ) {
				pendingOpen = index;
				var capturedIndex = index;
				closePanel( function () {
					// Only proceed if this is still the queued open
					// (user may have clicked again during the close).
					if ( pendingOpen !== capturedIndex ) return;
					pendingOpen = null;
					doOpen( capturedIndex );
				} );
				return;
			}

			// Nothing open — animate in directly.
			doOpen( index );
		}

		// Card click / keyboard handlers.
		cards.forEach( function ( card ) {
			card.addEventListener( 'click', function () {
				openPanel( card.dataset.teamCard );
			} );
			card.addEventListener( 'keydown', function ( e ) {
				if ( e.key === 'Enter' || e.key === ' ' ) {
					e.preventDefault();
					openPanel( card.dataset.teamCard );
				}
			} );
		} );

		// Close button handlers.
		container.querySelectorAll( '[data-bio-close]' ).forEach( function ( btn ) {
			btn.addEventListener( 'click', function () {
				pendingOpen = null;
				closePanel();
			} );
		} );

		// Escape key.
		document.addEventListener( 'keydown', function ( e ) {
			if ( e.key === 'Escape' ) {
				pendingOpen = null;
				closePanel();
			}
		} );
	}

	function init() {
		document.querySelectorAll( '[data-team-grid]' ).forEach( initGrid );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();
